import { useState } from "react";

import { useDispatch, useSelector } from "react-redux";

import { colorsPalettes } from "@similarweb/styles";
import { Modal } from "@similarweb/ui-components";
import { Flex, Stack, Text } from "@similarweb/ui-components/foundations";

import type { CrmIntegrationProvider } from "app/pages/sales-intelligence/domain/crm-integration/types/provider";
import {
    ExportPreviewEmptyState
} from "app/pages/sales-intelligence/sub-modules/contacts-search/components/ContactsTableActionsPanel/ExportContactsToCrm/shared/ExportPreviewEmptyState";
import {
    ExportPreviewFooter
} from "app/pages/sales-intelligence/sub-modules/contacts-search/components/ContactsTableActionsPanel/ExportContactsToCrm/shared/ExportPreviewFooter";
import { useTranslation } from "pro-features/components/WithTranslation";


import { ACCOUNTS_EXPORT_PREVIEW_MODAL_CLASS } from "../../constants";
import { accountPreviewChanged, applyBulkAccountsSelection } from "../../store/action-creators";
import {
    selectAccountsExportCount,
    selectAccountsPreview,
    selectCanCreateNewAccountForExport,
    selectIsAccountsExportBlocked,
    selectMatchedAccountsExportCount,
} from "../../store/selectors";
import type { AccountExportPreview, AccountsExportBulkSelection } from "../../types";
import { BULK_CHOOSE } from "../../types";
import { AccountExportPreviewItem } from "../AccountExportPreviewItem/AccountExportPreviewItem";
import { AccountsExportBulkActions } from "../AccountsExportBulkActions/AccountsExportBulkActions";
import {
    accountsModalStyles,
    StyledAccountsExportPreviewContent,
    StyledAccountsList,
    StyledAccountsListHeader,
} from "../styles";

type AccountsExportPreviewModalProps = {
    isOpen: boolean;
    integrationName: CrmIntegrationProvider;
    onExport(): void;
    onCancel(): void;
};

export const AccountsExportPreviewModal = (props: AccountsExportPreviewModalProps) => {
    const { isOpen, integrationName, onExport, onCancel } = props;

    const translate = useTranslation();
    const dispatch = useDispatch();

    const preview = useSelector(selectAccountsPreview);
    const total = useSelector(selectAccountsExportCount);
    const matchedCount = useSelector(selectMatchedAccountsExportCount);
    const canCreateNewAccount = useSelector(selectCanCreateNewAccountForExport);
    const isExportBlocked = useSelector(selectIsAccountsExportBlocked);

    const [bulkSelection, setBulkSelection] = useState<AccountsExportBulkSelection>(BULK_CHOOSE);

    const handleAccountChange = (item: AccountExportPreview) => {
        const prev = preview.find((entry) => entry.domain === item.domain);
        dispatch(accountPreviewChanged(item));
        if (prev?.accountId !== item.accountId) {
            setBulkSelection(BULK_CHOOSE);
        }
    };

    const handleBulkChange = (selection: AccountsExportBulkSelection) => {
        setBulkSelection(selection);
        if (selection !== BULK_CHOOSE) {
            dispatch(applyBulkAccountsSelection(selection));
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            shouldCloseOnOverlayClick={false}
            onRequestClose={onCancel}
            customStyles={accountsModalStyles}
            data-automation="accounts-export-preview-modal"
        >
            <Stack
                className={ACCOUNTS_EXPORT_PREVIEW_MODAL_CLASS}
                width="100%"
                maxHeight="100%"
                minHeight="0"
                overflow="hidden"
            >
                <Modal.Header withBorder>
                    <Modal.Title>
                        {translate("si.advanced_search.accounts_export_preview_modal.title")}
                    </Modal.Title>
                </Modal.Header>
                {total > 0 ? (
                    <StyledAccountsExportPreviewContent>
                        <Stack gap="24px">
                            <Stack gap="24px" paddingX="24px">
                                <Text fontVariant="body1" color={colorsPalettes.carbon[400]}>
                                    {translate(
                                        `si.advanced_search.accounts_export_preview_modal.description.${integrationName}`,
                                        { matchedCount, total },
                                    )}
                                </Text>

                                <Flex alignItems="center" gap="16px">
                                    <Text
                                        fontVariant="subtitle1"
                                        color={colorsPalettes.carbon[400]}
                                    >
                                        {translate("si.crm_export_preview_modal.apply_to_all.info")}
                                    </Text>

                                    <AccountsExportBulkActions
                                        value={bulkSelection}
                                        onChange={handleBulkChange}
                                    />
                                </Flex>
                            </Stack>

                            <div>
                                <StyledAccountsListHeader>
                                    <Text fontVariant="listSectionTitle" color={colorsPalettes.carbon[300]}>
                                        {translate("si.advanced_search.accounts_export_preview_modal.columns.company", {
                                            count: total
                                        })}
                                    </Text>
                                    <Text fontVariant="listSectionTitle" color={colorsPalettes.carbon[300]}>
                                        {translate(
                                            `si.advanced_search.accounts_export_preview_modal.columns.account.${integrationName}`,
                                        )}
                                    </Text>
                                </StyledAccountsListHeader>
                                <StyledAccountsList>
                                    {preview.map((item, index) => (
                                        <AccountExportPreviewItem
                                            key={item.domain}
                                            item={item}
                                            index={index}
                                            integrationName={integrationName}
                                            canCreateNewAccount={canCreateNewAccount}
                                            onChange={handleAccountChange}
                                        />
                                    ))}
                                </StyledAccountsList>
                            </div>
                        </Stack>
                    </StyledAccountsExportPreviewContent>
                ) : (
                    <ExportPreviewEmptyState
                        title={translate("si.advanced_search.accounts_export_preview_modal.empty_list_title")}
                        description={translate(
                            "si.advanced_search.accounts_export_preview_modal.empty_list_description",
                        )}
                        subtitle={translate("si.advanced_search.accounts_export_preview_modal.empty_list_subtitle")}
                    />
                )}
                <ExportPreviewFooter
                    countLabel={translate("si.advanced_search.accounts_export_preview_modal.export_count")}
                    count={total}
                    isExportDisabled={isExportBlocked || total === 0}
                    isTooltipShown={isExportBlocked}
                    tooltipContent={translate(
                        "si.advanced_search.accounts_export_preview_modal.export_button_disabled",
                    )}
                    onCancel={onCancel}
                    onExport={onExport}
                    data-automation="accounts-export-preview-footer"
                />
            </Stack>
        </Modal>
    );
};
